import type { RunbookTask } from '@/types/entities'
import { normaliseLabel } from './runbookOptions'

export function validateDependencies(
    input: string,
    allTasks: (RunbookTask & { _index?: number })[],
    currentTaskNo: number | null
): string | null {
    if (currentTaskNo === 1) return null

    if (!input.trim()) {
        return 'Dependencies cannot be blank'
    }

    const parts = input.split(',').map((part) => part.trim()).filter((part) => part !== '')

    if (parts.length === 0) {
        return 'Dependencies cannot be blank'
    }

    const validTaskNos = allTasks
        .map((task) => task.taskNo)
        .filter((taskNo): taskNo is number => taskNo !== null)

    for (const part of parts) {
        if (!/^\d+$/.test(part)) {
            return 'Dependencies must be comma-separated numbers only (no letters)'
        }
    }

    const taskNos = parts.map((part) => parseInt(part, 10))
    for (const taskNo of taskNos) {
        if (!validTaskNos.includes(taskNo)) {
            return `Task ${taskNo} does not exist`
        }
        if (taskNo === currentTaskNo) {
            return 'Task cannot depend on itself'
        }
    }

    // Option branches are alternatives: exactly one of them runs. A dependency from one
    // branch into another therefore describes work that will never happen — at recovery
    // time the other branch is skipped, which SATISFIES the dependency, so nothing
    // deadlocks and nothing complains. The task simply runs having waited for something
    // nobody did. Catching it here, while it is being typed, is the only place it is
    // cheap to catch.
    const current = allTasks.find((task) => task.taskNo === currentTaskNo)
    const currentGroup = normaliseLabel(current?.optionGroup)
    const currentBranch = normaliseLabel(current?.optionName)
    if (currentGroup && currentBranch) {
        for (const taskNo of taskNos) {
            const dep = allTasks.find((task) => task.taskNo === taskNo)
            const depGroup = normaliseLabel(dep?.optionGroup)
            const depBranch = normaliseLabel(dep?.optionName)
            if (depGroup === currentGroup && depBranch && depBranch !== currentBranch) {
                return `Task ${taskNo} is on the "${depBranch}" branch — a task cannot depend on an alternative it will never run alongside`
            }
        }
    }

    const dependencyGraph = new Map<number, number[]>()
    allTasks.forEach((task) => {
        if (task.taskNo !== null) {
            const deps =
                task.taskNo === currentTaskNo
                    ? taskNos
                    : (task.dependencies || '')
                          .split(',')
                          .map((d) => d.trim())
                          .filter((d) => d !== '')
                          .map((d) => parseInt(d, 10))
                          .filter((d) => !isNaN(d) && validTaskNos.includes(d))
            dependencyGraph.set(task.taskNo, deps)
        }
    })

    const hasCycle = (startTaskNo: number, visited: Set<number>, recStack: Set<number>): boolean => {
        visited.add(startTaskNo)
        recStack.add(startTaskNo)

        const deps = dependencyGraph.get(startTaskNo) || []
        for (const dep of deps) {
            if (!visited.has(dep)) {
                if (hasCycle(dep, visited, recStack)) return true
            } else if (recStack.has(dep)) {
                return true
            }
        }

        recStack.delete(startTaskNo)
        return false
    }

    if (currentTaskNo !== null) {
        const visited = new Set<number>()
        const recStack = new Set<number>()
        if (hasCycle(currentTaskNo, visited, recStack)) {
            return 'Circular dependency detected - this would create a dependency cycle'
        }
    }

    return null
}
